import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CATEGORIES, ACCENTS, categoryBySlug, subName } from "@/data/catalog";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const Sidebar = ({ activeCat, activeSub }) => (
  <aside className="hidden w-60 shrink-0 lg:block" data-testid="category-sidebar">
    <div className="sticky top-32 space-y-5">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Collections</p>
      {CATEGORIES.map((c) => {
        const accent = ACCENTS[c.accent];
        const isActive = c.slug === activeCat;
        return (
          <div key={c.slug}>
            <Link
              to={`/shop/${c.slug}`}
              data-testid={`sidebar-${c.slug}`}
              className={`font-display text-sm font-bold uppercase tracking-wide transition-colors ${
                isActive ? accent.text : "text-zinc-300 hover:text-white"
              }`}
            >
              {c.name}
            </Link>
            {isActive && c.subs.length > 0 && (
              <div className="mt-2 space-y-1.5 border-l border-white/10 pl-3">
                {c.subs.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/shop/${c.slug}/${s.slug}`}
                    data-testid={`sidebar-${c.slug}-${s.slug}`}
                    className={`block text-xs transition-colors ${
                      s.slug === activeSub ? "text-brand-yellow" : "text-zinc-500 hover:text-brand-cyan"
                    }`}
                  >
                    {s.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  </aside>
);

const Category = ({ catAlias, subAlias }) => {
  const params = useParams();
  const cat = catAlias || params.cat;
  const sub = subAlias || params.sub;
  const category = categoryBySlug(cat);
  const [products, setProducts] = useState(null);

  useEffect(() => {
    setProducts(null);
    fetchProducts(cat, sub).then(setProducts).catch(() => setProducts([]));
  }, [cat, sub]);

  if (!category) {
    return (
      <div className="mx-auto max-w-7xl px-6 pt-48 pb-32 text-center">
        <p className="font-display text-4xl font-black uppercase text-zinc-500">Category not found</p>
        <Link to="/" className="mt-4 inline-block font-mono text-xs uppercase tracking-widest text-brand-cyan">
          Back to home
        </Link>
      </div>
    );
  }

  const accent = ACCENTS[category.accent];
  const title = sub ? subName(cat, sub) : category.name;

  return (
    <div data-testid="category-page" className="mx-auto max-w-7xl px-4 pb-24 pt-40 sm:px-6">
      <header className="halftone relative mb-10 overflow-hidden border border-white/10 bg-ink-2 p-8 sm:p-12">
        <span className={`absolute left-4 top-4 h-4 w-4 border-l-2 border-t-2 ${accent.border}`} />
        <span className={`absolute bottom-4 right-4 h-4 w-4 border-b-2 border-r-2 ${accent.border}`} />
        <p className={`font-mono text-xs uppercase tracking-[0.4em] ${accent.text}`}>
          {sub ? category.name : "Collection"}
        </p>
        <motion.h1
          key={title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-6xl"
        >
          {title}
        </motion.h1>
        <p className="mt-3 max-w-xl text-sm text-zinc-400">{category.blurb}</p>
      </header>

      <div className="mb-8 flex flex-wrap gap-2 lg:hidden">
        <Link
          to={`/shop/${cat}`}
          data-testid="subchip-all"
          className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider ${
            !sub ? `${accent.border} ${accent.text}` : "border-white/15 text-zinc-400"
          }`}
        >
          All
        </Link>
        {category.subs.map((s) => (
          <Link
            key={s.slug}
            to={`/shop/${cat}/${s.slug}`}
            data-testid={`subchip-${s.slug}`}
            className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider ${
              sub === s.slug ? `${accent.border} ${accent.text}` : "border-white/15 text-zinc-400"
            }`}
          >
            {s.name}
          </Link>
        ))}
      </div>

      <div className="flex gap-10">
        <Sidebar activeCat={cat} activeSub={sub} />
        <div className="flex-1">
          {products === null ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-72 animate-pulse border border-white/10 bg-ink-2" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="halftone border border-dashed border-white/15 p-16 text-center">
              <p className="font-display text-2xl font-bold uppercase text-zinc-500">
                Products dropping soon
              </p>
              <p className="mt-2 font-mono text-xs uppercase tracking-widest text-zinc-600">
                Ask us about this item via Bulk Inquiry
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="product-grid">
              {products.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Category;
